// Mentor inbox — view and respond to connection requests
import { useState } from 'react';
import { Inbox, CheckCircle, XCircle, Clock, MessageCircle, ChevronRight } from 'lucide-react';
import { useMentorshipRequests, useRespondToRequest, EXPERTISE_OPTIONS, type MentorshipRequest } from '@/hooks/useMentorship';
import { useAuthStore } from '@/stores/authStore';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { ThinPage } from '@/components/common/PageQuality';

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pending:   { label: 'Pending',   color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  accepted:  { label: 'Accepted',  color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  declined:  { label: 'Declined',  color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
};

function RequestCard({
  req,
  role,
  onRespond,
}: {
  req: MentorshipRequest;
  role: 'mentor' | 'mentee';
  onRespond: (id: string, action: 'accepted' | 'declined' | 'completed') => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const expertiseLabel = EXPERTISE_OPTIONS.find(o => o.value === req.expertise_sought)?.label ?? req.expertise_sought;
  const status = STATUS_LABEL[req.status] ?? STATUS_LABEL.pending;
  const date = new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header row */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
      >
        {/* Avatar */}
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C8102E]/10 flex items-center justify-center">
          <span className="text-base font-bold text-[#C8102E]">
            {(role === 'mentor' ? req.mentee_name : req.mentor_name)?.charAt(0).toUpperCase()}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white text-sm">
              {role === 'mentor' ? req.mentee_name : req.mentor_name}
            </span>
            {expertiseLabel && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#0047AB]/10 text-[#0047AB] dark:bg-blue-900/30 dark:text-blue-400">
                {expertiseLabel}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{req.message}</p>
        </div>

        <div className="flex-shrink-0 flex items-center gap-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.color}`}>
            {status.label}
          </span>
          <span className="text-xs text-gray-400">{date}</span>
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-4 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Their message</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{req.message}</p>
          </div>

          {req.status === 'accepted' && (
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-sm text-green-700 dark:text-green-400">
              <span className="font-medium">Connected!</span> You can now exchange contact details directly.
            </div>
          )}

          {/* Actions */}
          {role === 'mentor' && req.status === 'pending' && (
            <div className="flex gap-2">
              <button
                onClick={() => onRespond(req.id, 'accepted')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Accept
              </button>
              <button
                onClick={() => onRespond(req.id, 'declined')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Decline
              </button>
            </div>
          )}

          {role === 'mentor' && req.status === 'accepted' && (
            <button
              onClick={() => onRespond(req.id, 'completed')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <CheckCircle className="w-4 h-4 text-blue-500" />
              Mark as completed
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function MentorshipInbox() {
  const { token } = useAuthStore();
  const [role, setRole] = useState<'mentor' | 'mentee'>('mentor');
  const { data, isLoading, refetch } = useMentorshipRequests(role);
  const respond = useRespondToRequest();

  if (!token) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        {/* Gated, content-free view — nothing here to index. */}
        <SEO title="Mentorship inbox" description="Sign in to view your mentorship inbox." noindex />
        <ThinPage reason="mentorship inbox viewed logged-out" />
        <p className="text-gray-500 dark:text-gray-400 mb-4">Sign in to view your mentorship inbox.</p>
        <Link to="/login" className="px-5 py-2 rounded-lg bg-[#C8102E] text-white text-sm font-medium hover:bg-[#A00D25] transition-colors">
          Sign in
        </Link>
      </div>
    );
  }

  const requests = data?.requests ?? [];
  const pending = requests.filter(r => r.status === 'pending').length;

  async function handleRespond(id: string, action: 'accepted' | 'declined' | 'completed') {
    await respond.mutateAsync({ id, status: action });
    refetch();
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/*
        A personal request queue. Personalised, gated, and different for every
        user — never index it, and don't serve ads against it.
      */}
      <SEO
        title="Mentorship inbox"
        description="View and respond to your mentorship connection requests."
        noindex
      />
      <ThinPage reason="private mentorship inbox" />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#C8102E]" />
            Mentorship Inbox
            {pending > 0 && (
              <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-[#C8102E] text-white">
                {pending}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {role === 'mentor' ? 'Requests from people who want to connect with you' : 'Your outgoing connection requests'}
          </p>
        </div>
        <Link
          to="/guides/finding-bilingual-mentors"
          className="text-sm text-[#0047AB] hover:underline"
        >
          Career guides
        </Link>
      </div>

      {/* Role tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg mb-6 w-fit">
        {(['mentor', 'mentee'] as const).map(r => (
          <button
            key={r}
            onClick={() => setRole(r)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
              role === r
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            As {r}
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16">
          <MessageCircle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">
            {role === 'mentor' ? 'No requests yet' : 'You haven\'t sent any requests'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {role === 'mentor' ? 'Make sure your profile is published and visible.' : 'Find someone through the career guide and send them a connection request.'}
          </p>
          {role === 'mentee' && (
            <Link
              to="/guides/finding-bilingual-mentors"
              className="mt-4 inline-block px-5 py-2 rounded-lg bg-[#C8102E] text-white text-sm font-medium hover:bg-[#A00D25] transition-colors"
            >
              Read the career guide
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(req => (
            <RequestCard
              key={req.id}
              req={req}
              role={role}
              onRespond={handleRespond}
            />
          ))}
        </div>
      )}
    </div>
  );
}
